import React, { createContext, useState, useEffect } from "react";

export const SystemConfigContext = createContext();

export const SystemConfigProvider = ({ children }) => {
  // ✅ Detect first page load (including reload) using sessionStorage
  const [currentConnectionId, setCurrentConnectionId] = useState(null);

  // ✅ Enhanced: Detect both page reload AND new connection ID
  const getSavedState = (key, defaultValue, connectionId) => {
    if (!connectionId) return defaultValue;
    
    // Check if this is a different connection than what we have saved
    const lastConnectionId = sessionStorage.getItem("lastConnectionId");
    
    // Reset if:
    // 1. No previous connection ID (fresh start)
    // 2. Different connection ID (switching to new connection)
    // 3. Page reload detected
    const navigation = performance.getEntriesByType("navigation")[0];
    const isPageReload = navigation && navigation.type === "reload";
    
    if (!lastConnectionId || lastConnectionId !== connectionId || isPageReload) {
      //console.log(`Resetting ${key} for new connection:`, connectionId);
      sessionStorage.setItem("lastConnectionId", connectionId);
      return defaultValue;
    }
    
    // Load from storage for same connection
    const connectionKey = `${connectionId}_${key}`;
    const saved = sessionStorage.getItem(connectionKey);
    return saved ? JSON.parse(saved) : defaultValue;
  };


  // Default hardware
  const defaultHardwareItems = [
  { id: 1,  name: 'Bell / Tannoy',    quantity: 0, category: 'Audio',    icon: '/images/bell.jpg' },
  { id: 2,  name: 'Mobile App',       quantity: 0, category: 'Software', icon: '/images/mobileapp.jpg' },
  { id: 3,  name: 'Mobile App Addon', quantity: 0, category: 'Software', icon: '📱' },
  { id: 4,  name: 'T54W',             quantity: 0, category: 'Phone',    icon: '/images/t54w.png' },
  { id: 5,  name: 'T57W',             quantity: 0, category: 'Phone',    icon: '/images/t57w.jpg' },
  { id: 6,  name: 'T58W',             quantity: 0, category: 'Phone',    icon: '/images/t58w.jpg' },
  { id: 7,  name: 'YEALINK T88',      quantity: 0, category: 'Phone',    icon: '/images/Yealink_new.jpg' },
  { id: 8,  name: 'GRANDSTREAM 3470', quantity: 0, category: 'Phone',    icon: '/images/Grandstream_3470.jpg' },
  { id: 9,  name: 'GRANDSTREAM 3480', quantity: 0, category: 'Phone',    icon: '/images/3480.jpg' },
  { id: 10, name: 'CISCO 9871',       quantity: 0, category: 'Phone',    icon: '/images/CISCO.jpeg' },
  { id: 11, name: 'Yealink W73H DECT',  quantity: 0, category: 'Phone',    icon: '/images/ipdect.jpg' },
  { id: 12, name: 'IP DECT Base',     quantity: 0, category: 'Phone',    icon: '/images/ipdect.jpg' },
  { id: 13, name: 'Wi-Fi Booster',    quantity: 0, category: 'Network',  icon: '/images/wifibooster.jpg' },
  { id: 14, name: 'Headsets',         quantity: 0, category: 'Audio',    icon: '/images/headset.jpg' },
  { id: 15, name: 'Fibre TV/Wallboard', quantity: 0, category: 'Display', icon: '/images/fibretv.jpg' },
  { id: 16, name: 'iPad',             quantity: 0, category: 'Tablet',   icon: '/images/ipad.png' },
  { id: 17, name: 'Fibre Audio',      quantity: 0, category: 'Audio',    icon: '🎧' },
  { id: 18, name: 'Add On',           quantity: 0, category: 'Display',  icon: '/images/fibretv.jpg' },
];

  const defaultAdditionalHardware = [
    { id: 'fibre', name: 'Fibre', quantity: 1, category: 'Music Module', icon: '/images/music.svg' },
    { id: 'media-player', name: 'Media Player', quantity: 1, category: 'Music Module', icon: '/images/module.svg' },
    { id: 'aa-voice-over', name: 'AA Voice Over', quantity: 1, category: 'Music Module', icon: '/images/music.svg' },
    { id: 'site-link-licence', name: 'Site Link Licence', quantity: 0, category: 'Site Link Licence', icon: '/images/sitelink.png' },
    { id: 'analogue-devices', name: 'Analogue Devices', quantity: 0, category: 'Site Link Licence', icon: '/images/sitelink.png' },
    { id: 'ups', name: 'UPS', quantity: 1, category: 'Protection', icon: '/images/protection.svg' },
    { id: 'surge-protection', name: 'Surge protection', quantity: 1, category: 'Protection', icon: '/images/protection.svg' },
    { id: 'pancode', name: 'Pancode', quantity: 0, category: 'Door entry', icon: '/images/doorentry.png' },
    { id: '1-button-entry', name: '1 Button Entry', quantity: 0, category: 'Door entry', icon: '/images/doorentry.png' },
    { id: 'ucm-ip-pbx', name: 'UCM IP PBX', quantity: 1, category: 'UCM IP PBX', icon: '/images/ucmippbx.jpg' },
    { id: 'voicemail-ports', name: 'Voicemail ports', quantity: 0, category: 'UCM IP PBX', icon: '/images/ucmippbx.jpg' },
    { id: 'wifi-enabled', name: 'Draytek 2865LAC', quantity: 0, category: 'Router', icon: '/images/router.png' },
    { id: '8-port-poe', name: 'Netgear GS108LP', quantity: 0, category: 'POE', icon: '/images/poe.png' },
    { id: '24-port-poe', name: '24 port POE', quantity: 0, category: 'POE', icon: '/images/poe.png' },
    { id: 'cat5e-cable-5m', name: 'Cat 5E Cable (5m)', quantity: 0, category: 'Cabling', icon: '/images/cat5e.jpg' },
    { id: 'system-moves', name: 'System Moves', quantity: 0, category: 'System Moves', icon: '/images/systemmoves.png' },
    { id: 'cat5', name: 'Cat 5', quantity: 0, category: 'System Moves', icon: '/images/systemmoves.png' },
  ];
   const defaultTelecomServices = [
    {
      id: Date.now(),
      description: '',
      service_type: '',
      existing_tel_no: '',
      quantity: '',
      postcode: ''
    }
  ];

  // ----------------- States -----------------
  const [lineRequirements, setLineRequirements] = useState(() =>
    getSavedState("lineRequirements", {
      existingIsdn2Channels: 0,
      newConversionIsdn2Channel: 0,
      existingIsdn30Channels: 0,
      newIsdn30Channel: 0,
    })
  );

  const [currentLines, setCurrentLines] = useState(() =>
    getSavedState("currentLines", {
      hostedLifetimeLicenses: 0,
      analoguePstnLines: 0,
      adslFttcBroadband: 0,
      new4g5gBroadband: 0,
      fttpSogeaBroadband: 0,
    })
  );

  const [dataBroadband, setDataBroadband] = useState(() =>
    getSavedState("dataBroadband", {
      starlinkBroadband: 0,
      leasedLines: 0,
    })
  );

  const [serviceChargesData, setServiceChargesData] = useState(() =>
    getSavedState("serviceCharge", {
      service_charge: 0,
      equipment_rentals: 0,
      broadband: 0,
      media: 0,
      maintenance: 0,
      utilities: 0,
    })
  );

  const [leaseCalculatorData, setLeaseCalculatorData] = useState(() =>
    getSavedState("leaseCalculator", {
      paymentProfile: '84',
      networkPayment: '12',
      discountLoad: 0,
      currentCallCharges: 0,
      estimatedCallCharges: 0,
      networkSettlementContribution: '0',
      adjustedRental: 0,
      actualLeasePayment: 0,
      actualProfit: 0,
      salesRepCommission: 0,
      current_lease_rental: 0,
      current_mobile_charges: 0,
      current_service_charges: 0,
      current_maintenance: 0,
      other_charges: 0,
      current_total_cost: 0,
      estimated_mobile_charges: 0,
      new_service_charges: 0,
      leased_line: 0,
      it_service_charge: 0,
      digital_services:null
    })
  );
  const defaultDigitalServices = {
    webHostingCost: 0,
    licenceServices: {},
    axoSignalPackage: null,
    totalNewDigitalCost: 0,
    totalFibreDigitalRevenue: 0,
  };
  const defaultItServices = {
    numberOfStations: 0,
    selectedPackages: ['it_support'],
    licenceQuantities: {},
  };

const [digitalServicesData, setDigitalServicesData] = useState(() =>
  getSavedState("digitalServices", defaultDigitalServices)
);

const [savedDigitalServices, setSavedDigitalServices] = useState(() =>
  getSavedState("digitalServices", defaultDigitalServices)
);
  const [telecomServices, setTelecomServices] = useState(() =>
    getSavedState("telecomServices", defaultTelecomServices)
  );

  const [hardwareItems, setHardwareItems] = useState(() =>
    getSavedState("hardwareItems", defaultHardwareItems)
  );

  const [additionalHardware, setAdditionalHardware] = useState(() =>
    getSavedState("additionalHardware", defaultAdditionalHardware)
  );
  // After the other useState declarations (around line where telecomServices is defined)
  const [notes, setNotes] = useState(() =>
    getSavedState("notes", "") ?? ""
  );

  const [itServicesData, setItServicesData] = useState(() =>
    getSavedState("itServices", defaultItServices)
  );

  // ----------------- Persist to sessionStorage -----------------
  useEffect(() => { sessionStorage.setItem("lineRequirements", JSON.stringify(lineRequirements)); }, [lineRequirements]);
  useEffect(() => { sessionStorage.setItem("currentLines", JSON.stringify(currentLines)); }, [currentLines]);
  useEffect(() => { sessionStorage.setItem("dataBroadband", JSON.stringify(dataBroadband)); }, [dataBroadband]);
  useEffect(() => { sessionStorage.setItem("hardwareItems", JSON.stringify(hardwareItems)); }, [hardwareItems]);
  useEffect(() => { sessionStorage.setItem("additionalHardware", JSON.stringify(additionalHardware)); }, [additionalHardware]);
  useEffect(() => { sessionStorage.setItem("serviceCharge", JSON.stringify(serviceChargesData)); }, [serviceChargesData]);
  useEffect(() => { sessionStorage.setItem("leaseCalculator", JSON.stringify(leaseCalculatorData)); }, [leaseCalculatorData]);
  useEffect(() => { 
    sessionStorage.setItem("telecomServices", JSON.stringify(telecomServices)); 
  }, [telecomServices]);
  useEffect(() => {
  sessionStorage.setItem("digitalServices", JSON.stringify(digitalServicesData));
}, [digitalServicesData]);
useEffect(() => { sessionStorage.setItem("notes", JSON.stringify(notes)); }, [notes]);
useEffect(() => { sessionStorage.setItem("itServices", JSON.stringify(itServicesData)); }, [itServicesData]);
  // ----------------- Reset Function -----------------
  const resetSystemConfig = () => {
    setLineRequirements({
      existingIsdn2Channels: 0,
      newConversionIsdn2Channel: 0,
      existingIsdn30Channels: 0,
      newIsdn30Channel: 0,
    });

    setCurrentLines({
      hostedLifetimeLicenses: 0,
      analoguePstnLines: 0,
      adslFttcBroadband: 0,
      new4g5gBroadband: 0,
      fttpSogeaBroadband: 0,
    });

    setDataBroadband({ starlinkBroadband: 0, leasedLines: 0 });
    setHardwareItems(defaultHardwareItems);
    setAdditionalHardware(defaultAdditionalHardware);

    setServiceChargesData({
      service_charge: 0,
      equipment_rentals: 0,
      broadband: 0,
      media: 0,
      maintenance: 0,
      utilities: 0,
    });

    setLeaseCalculatorData({
      paymentProfile: '84',
      networkPayment: '0',
      discountLoad: 0,
      currentCallCharges: 0,
      estimatedCallCharges: 0,
      networkSettlementContribution: '0',
      adjustedRental: 0,
      actualLeasePayment: 0,
      actualProfit: 0,
      salesRepCommission: 0,
      current_lease_rental: 0,
      current_mobile_charges: 0,
      current_service_charges: 0,
      current_maintenance: 0,
      other_charges: 0,
      current_total_cost: 0,
      estimated_mobile_charges: 0,
      new_service_charges: 0,
      leased_line: 0,
      it_service_charge: 0,
      digital_services: null,
    });
    setDigitalServicesData(defaultDigitalServices); 
    setTelecomServices(defaultTelecomServices);
    setItServicesData(defaultItServices);
    setNotes('');

    // Clear sessionStorage
    sessionStorage.removeItem("lineRequirements");
    sessionStorage.removeItem("currentLines");
    sessionStorage.removeItem("dataBroadband");
    sessionStorage.removeItem("hardwareItems");
    sessionStorage.removeItem("additionalHardware");
    sessionStorage.removeItem("serviceCharge");
    sessionStorage.removeItem("leaseCalculator");
    sessionStorage.removeItem("telecomServices");
    sessionStorage.removeItem("digitalServices");
    sessionStorage.removeItem("itServices");
    sessionStorage.removeItem("notes");
    
  };

  return (
    <SystemConfigContext.Provider
      value={{
        lineRequirements,
        setLineRequirements,
        currentLines,
        setCurrentLines,
        dataBroadband,
        setDataBroadband,
        hardwareItems,
        setHardwareItems,
        additionalHardware,
        setAdditionalHardware,
        serviceChargesData,
        setServiceChargesData,
        leaseCalculatorData,
        telecomServices, // ✅ NEW: Add telecom services to context
        setTelecomServices, // ✅ NEW: Add setter for telecom services
        setLeaseCalculatorData,
        digitalServicesData,  
         savedDigitalServices,        // ← ADD THIS
      setSavedDigitalServices,     // ← add
        setDigitalServicesData,
        resetSystemConfig,
        notes,
        setNotes,
        itServicesData,
        setItServicesData
      }}
    >
      {children}
    </SystemConfigContext.Provider>
  );
};
